import { minBy } from 'es-toolkit';

import { Direction2D, Point2D, Vector2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { SnappingLevel } from '../../../store/slices/applicationSlice';
import { GeometricEcosystem } from '../../layout-geometry/ecosystem/GeometricEcosystem';
import { StageOneTrussEnvelope } from '../../layout-geometry/truss-envelopes/calculation/stage1';
import { TrussEnvelopeSnappingSettings } from '../../layout-geometry/truss-envelopes/types';
import { getClosestEntitySnapTarget } from '../closestTarget';
import { EntitySnapTarget, EntitySnapTargetType } from '../EntitySnapTarget';
import { EntitySnapTargetSource } from '../EntitySnapTargetSource';
import { EntitySnapTargetSelectionSettings } from '../settings';
import {
  applyDistanceSnaps,
  DistanceSnappingSettings,
  getAutomaticDistanceSnappingSettings as inferAutomaticDistanceSnappingSettings,
} from './distance';

export class TrussEnvelopeSnapper {
  private readonly ecosystem: GeometricEcosystem;

  private readonly getDistanceSettings: (target: EntitySnapTargetType) => DistanceSnappingSettings;

  private readonly targets: EntitySnapTarget[];

  constructor(
    envelopeSnappingSettings: TrussEnvelopeSnappingSettings,
    pointSnappingSettings: EntitySnapTargetSelectionSettings,
    snappingLevel: SnappingLevel,
    ecosystem: GeometricEcosystem,
  ) {
    this.ecosystem = ecosystem;

    this.getDistanceSettings = (target) =>
      envelopeSnappingSettings.distanceSettings === 'automatic'
        ? inferAutomaticDistanceSnappingSettings(target, envelopeSnappingSettings.lockedTarget ? snappingLevel : null)
        : envelopeSnappingSettings.distanceSettings;

    this.targets = new EntitySnapTargetSource(this.ecosystem).getActiveTargetsForTrussEnvelopeSnapping(
      pointSnappingSettings,
      envelopeSnappingSettings.lockedTarget ?? undefined,
    );
  }

  snapEnvelopeParallelToClosestTarget(definingPoint: Point2D) {
    const closestTarget = getClosestEntitySnapTarget(this.targets, definingPoint);
    if (closestTarget == null) return null;

    const projectedPoint = closestTarget.getProjectedPoint(definingPoint);
    if (projectedPoint == null) return null;

    const distanceToDefiningPoint = definingPoint.distanceTo(projectedPoint);
    const distanceSettings = this.getDistanceSettings(closestTarget.type);

    const offsetDistance = applyDistanceSnaps(distanceToDefiningPoint, distanceSettings);
    const offsetDirection = Direction2D.fromPoints(projectedPoint, definingPoint);
    const offsetVector = Vector2D.fromDirectionAndMagnitude(offsetDirection, offsetDistance);

    const snappedLine = closestTarget.getDefiningLine().translate(offsetVector);
    if (!this.ecosystem.getRoofAreas().some((area) => area.hasSegmentIntersectionWithLine(snappedLine))) return null;

    return new StageOneTrussEnvelope(definingPoint, snappedLine, {
      ecosystem: this.ecosystem,

      origin: {
        type: 'parallel-line',
        snappingTarget: closestTarget,
        actualSettings: {
          behaviorMode: 'closest-target',
          lockedTarget: this.targets.length === 1 ? this.targets[0] : null,
          distanceSettings,
        },
      },
    });
  }

  snapEnvelopeToClosestLineParallelToTargets(definingPoint: Point2D) {
    const options = this.targets
      .map((target) => {
        const projectedPoint = target.getProjectedPoint(definingPoint);
        if (projectedPoint == null) return null;

        const distanceToDefiningPoint = definingPoint.distanceTo(projectedPoint);
        const distanceSettings = this.getDistanceSettings(target.type);

        const offsetDistance = applyDistanceSnaps(distanceToDefiningPoint, distanceSettings);
        const offsetDirection = Direction2D.fromPoints(projectedPoint, definingPoint);
        const offsetVector = Vector2D.fromDirectionAndMagnitude(offsetDirection, offsetDistance);

        const snappedLine = target.getDefiningLine().translate(offsetVector);
        const snappedDistance = definingPoint.distanceToLine(snappedLine);

        return { target, distanceSettings, snappedDistance, snappedLine };
      })
      .filter(notNullish)
      .filter(({ snappedLine }) =>
        this.ecosystem.getRoofAreas().some((area) => area.hasSegmentIntersectionWithLine(snappedLine)),
      );

    const bestChoice = minBy(options, (line) => line.snappedDistance);
    if (bestChoice == null) return null;

    return new StageOneTrussEnvelope(definingPoint, bestChoice.snappedLine, {
      ecosystem: this.ecosystem,

      origin: {
        type: 'parallel-line',
        snappingTarget: bestChoice.target,
        actualSettings: {
          behaviorMode: 'closest-result',
          lockedTarget: this.targets.length === 1 ? this.targets[0] : null,
          distanceSettings: bestChoice.distanceSettings,
        },
      },
    });
  }
}
